import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spinner } from '@producer/ui-kit';

import { fetchCrew } from '../../crew/stores/crew.slice';
import { filterCrew } from '../../crew/lib/filter-crew';
import { CrewFilters } from '../../crew/components/crew-filters';
import { ToolBar } from '../../../components/toolbar/toolbar';
import { LoaderContainer } from '../../../routes/project/components/step-container';
import { ProtectedTeamItem } from './protected-team-item';
import { CrewCallTimes } from './crew-call-times';
import { Header } from '../../../components/table/styled-shared-table';
import { TeamContact } from '../../teams/models/team';
import { Character } from '../../../app/entities/character';
import { Contact } from '../../../app/entities/contact';

import type { Step } from '../../../app/entities/step';
import type { Project } from '../../../app/entities/project';
import type { CrewFilter } from '../../crew/lib/filter-crew';

import styles from './team-list.module.scss';
type TeamListProps = {
  project: Project;
  step: Step;
};

export const TeamList: React.FC<TeamListProps> = observer(({ step, project }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<CrewFilter>({});

  // Crew is not part of the project bootstrap payload (see `project-crew.route.tsx`), so this
  // screen fetches its own copy the same way the Crew step and Resources > Crew do.
  useEffect(() => {
    fetchCrew(project._id)
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [project._id]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <Spinner />
      </LoaderContainer>
    );
  }

  const characters = step.shots
    .map((shot) => shot.characters)
    .flat()
    .map((character) => character._id);
  const charactersIds = [...new Set(characters)];

  const cast = charactersIds
    ?.map((characterId) => {
      const fullCharacter = Character.getOne(characterId);
      if (!fullCharacter) return;

      if (!fullCharacter.cast) {
        return {
          email: '-',
          phone: '-',
          role: fullCharacter.name,
          contactId: null,
        };
      }

      const contact = Contact.getOne(fullCharacter.cast.contactId);

      return {
        ...fullCharacter.cast,
        email: contact?.email,
        phone: contact?.phone,
        role: fullCharacter.name,
      };
    })
    .filter((cast) => !!cast)
    .map((cast) => ({
      ...cast,
      _id: cast?.contactId,
    })) as TeamContact[];

  const visibleCrew = filterCrew(project.crewMembers, filter);

  // No grouping control on this screen, so `Tools` (the right-hand button group) is omitted
  // entirely rather than rendered empty.
  const crewFilters = (
    <ToolBar>
      <CrewFilters
        value={filter}
        onChange={setFilter}
        departments={project.crewDepartments}
        teams={project.crewTeams}
      />
    </ToolBar>
  );

  return (
    <div className={styles['container']}>
      <Header>
        <h1>Crew</h1>
      </Header>

      <div className={styles['teams-container']}>
        {!!project?.contacts?.length && (
          <ProtectedTeamItem
            name="Executive"
            contacts={project.contacts}
            step={step}
            meta={step.contactsMetaData}
          />
        )}

        <div>
          {crewFilters}

          <CrewCallTimes members={visibleCrew} step={step} />
        </div>

        {!!cast?.length && (
          <ProtectedTeamItem name="Cast" contacts={cast} step={step} meta={step.contactsMetaData} />
        )}
      </div>
    </div>
  );
});
